import React from "react";

import CTAButton from "src/components/ctabutton/ctabutton";
import ImageV2 from "src/components/image/image.v2";
import { MaterialIcon } from "src/components/icon";

import { getLanguageField } from "../../utils/language";
import type { SplitButtonsProps } from "./types";
import { ModuleHeadV2 } from "src/components/moduleHead/moduleHead.v2";

export default class SplitButtons extends React.Component<SplitButtonsProps> {
  BUTTON_MIN_HEIGHT = 44

  get buttonColors(){
    if(this.props.item?.cta_button_type === "primary") return this.props.item?.cta_text_color?.value
    if(this.props.item?.cta_button_type === "secondary") return this.props.item?.secondary_cta_text_color?.value
    if(this.props.item?.cta_button_type === "soft") return this.props.item?.soft_button_text_color?.value
  }

  get verticalAlignment(){
    const { vertical_alignment } = this.props.item;
    return vertical_alignment === "top" ? "items-start" : vertical_alignment === "bottom" ? "items-end" : "items-center";
  }

  get isFullWidth() {
    return this.props.item.width === 'full_screen';
  }

  get boxedText(){
    const { boxed_content } = this.props.item;
    return boxed_content === "text";
  }

  get innerPadding(){
    if (this.boxedText) return "p-6 sm:p-9";
    return "";
  }

  get wrapperPadding(){
    if (!this.isFullWidth) return "py-8";
    return "";
  }

  get boxedBackgroundColor(){
    if (this.boxedText) return this.props.item.boxed_background_color?.value;
    return undefined;
  }

  get rounded(){
    const { rounded_corners, boxed_content } = this.props.item;
    return rounded_corners && boxed_content === "text" ? "rounded-2xl" : "";
  }

  componentDidMount(): void {
    //Apply same height to all buttons FLM-4880
    let height = this.BUTTON_MIN_HEIGHT;
    this.props.item.split_cta_buttons_items.forEach((item) => {
      const element = document.getElementById(`button-${this.props.item.id}-${item.item.id}`)
      if(element && element?.clientHeight > height) height = element.clientHeight;
    })
    if(height > this.BUTTON_MIN_HEIGHT) {
      this.props.item.split_cta_buttons_items.forEach((item) => {
      let element = document.getElementById(`button-${this.props.item.id}-${item.item.id}`)
      if(element) element.style.height = `${height}px`;
      })
    }
  }

  render() {
    const moduleData = {
      title: getLanguageField(this.props?.item?.split_cta_buttons_translations, this.props.selectedLanguage, "title") as string | undefined,
      subtitle: getLanguageField(this.props?.item?.split_cta_buttons_translations, this.props.selectedLanguage, "subtitle") as string | undefined,
      description: getLanguageField(this.props?.item?.split_cta_buttons_translations, this.props.selectedLanguage, "body", true, true) as string | undefined,
      items: this.props?.item?.split_cta_buttons_items,
      alignment: this.props.item?.text_position,
      buttonType: this.props.item?.cta_button_type,
    }

    const colors = {
      titleColor: this.props?.item?.title_color?.value || "black",
      bodyColor: this.props?.item?.body_color?.value || "black",
      subtitleColor: this.props?.item?.subtitle_color?.value || "black",
      ctaTextColor: this.props?.item?.cta_text_color?.value,
      ctaBackgroundColor: this.props?.item?.cta_background_color?.value,
    }

    const TextColumn = () => <div className={`mx-auto w-full lg:mx-0 ${this.verticalAlignment} ${this.innerPadding} ${this.rounded}`} style={{ backgroundColor: this.boxedBackgroundColor }}>
        <ModuleHeadV2
          title={moduleData.title}
          subtitle={moduleData.subtitle}
          description={moduleData.description}
          color_title={colors.titleColor}
          color_subtitle={colors.subtitleColor}
          color_description={colors.bodyColor}
      />
      </div>

    return (
      <div className={`mx-auto max-w-7xl flex w-full ${this.wrapperPadding}`}>
        <div className={`grid grid-cols-1 gap-x-8 gap-y-4 lg:gap-y-16 lg:grid-cols-2 w-full ${this.verticalAlignment}`}>
          <TextColumn />
          <div className={`mx-auto grid w-full grid-cols-2 gap-y-6 gap-x-6 lg:mx-0 lg:max-w-none lg:pl-8`}>
            {
              moduleData.items && moduleData.items.length ?
                moduleData.items.map((item, idx: number) => {
                  const { translations } = item.item;

                  // FLM-6260 - split button icon like cta button icon.
                  const { id, icon_type, icon_position, material_icon } = item.item;
                  const iconMargin = icon_position === 'right' ? 'ml-2' : 'mr-2';
                  let icon = undefined;
                  if (icon_type === 'custom' && item.item?.icon?.filename_disk)
                    icon = <ImageV2 asSvgIcon={true} id={"ctasb_" + id} src={item?.item?.icon?.filename_disk} className={`w-4 h-4 ${iconMargin}`} />
                  else if (icon_type === 'material' && typeof material_icon === "string")
                    icon = <MaterialIcon className={`w-4 h-4 ${iconMargin}`} icon={material_icon} />;
                  else if (!icon && item.item.icon?.filename_disk) {
                    icon = <ImageV2 asSvgIcon={true} id={"ctasb_" + id} src={item?.item?.icon?.filename_disk} className={`w-4 h-4 ${iconMargin}`} />
                  }
                  const ctaButtonIconProps = icon_position === 'right' ? { after: icon } : { before: icon };
                  // partial-end FLM-6260 - split button icon like cta button icon.

                  const buttonText = getLanguageField(translations, this.props.selectedLanguage, "button_text");
                  const buttonLinkType = getLanguageField(item.item.translations, this.props.selectedLanguage, "button_link_type");
                  const buttonPageTranslations = buttonLinkType  !== "external" ? getLanguageField(item.item.translations, this.props.selectedLanguage, "button_page") as any : []
                  const href = buttonLinkType === "external" ? getLanguageField(item.item.translations, this.props.selectedLanguage, "button_link") : `/${getLanguageField(buttonPageTranslations.translations, this.props.selectedLanguage, "slug")}`
                  const className = moduleData.items.length === 1 ? "col-span-2" : "col-span-2 md:col-span-1";
                  const buttonBgColor = moduleData.buttonType === "soft" ? undefined : colors.ctaBackgroundColor;
                  const alignment = moduleData.buttonType === "soft" ? "!justify-center text-center sm:!justify-start sm:text-start" : "";
                  const noHrefClassname = !href ? "!pointer-events-none" : ""

                  return <div className={`relative flex w-full h-full ${className}`} key={`button-${idx}`} id={`button-${this.props.item.id}-${item.item.id}`}>
                    <CTAButton
                      {...ctaButtonIconProps}
                      className={`w-full h-full ${alignment} ${noHrefClassname}`}
                      text={buttonText as string}
                      type={moduleData.buttonType}
                      color={this.buttonColors}
                      border={moduleData.buttonType === "secondary" ? this.props.item?.secondary_cta_line_color?.value : undefined}
                      bgcolor={buttonBgColor}
                      link={href as string}
                    />
                  </div>
                })
                : null
            }
          </div>
        </div>
      </div>
    )
  }
}